import { TranslateService } from '@ngx-translate/core';
import { Action } from 'redux';
import { ActionsObservable, ofType, StateObservable } from 'redux-observable';
import { forkJoin, Observable, of, OperatorFunction } from 'rxjs';
import {
    catchError,
    concatMap,
    map,
    mergeMap,
    switchMap,
    tap,
} from 'rxjs/operators';

import { AuthService } from '../auth/auth.service';
import { DataFormService } from '../form/data.form.service';
import { Args, Data } from '../form/form.model';
import { TemplateFormService } from '../form/template.form.service';
import { Dependencies } from '../store';

import {
    ActionType,
    FormSaveAction,
    formSaveResult,
    PrintRetrieveInitDataAction,
    printRetrieveInitDataResult,
    ReportGenerateAction,
    reportGenerateResult,
    ReportRetrieveInitDataAction,
    reportRetrieveInitResultData,
    RetrieveInitDataAction,
    retrieveInitDataResult,
} from './tdcf-form.action';
import {
    FlowState,
    hasReviewWithin,
    HierarchyInfo,
    ManagerReportDataItem,
    Metadata,
    MetadataModelTransformer,
    Payload,
    PayloadExtended,
    PayloadModelTransformer,
    ReportArgs,
    reviewDateRangeFor,
    Text,
} from './tdcf.model';
import { TdcfService } from './tdcf.service';

interface ActionResult<T, U> {
    action: T;
    data: U;
}

const _retrieveMetadata =
    (deps: Dependencies) =>
    <T extends { templateId: number }>(
        source: Observable<T>,
    ): Observable<ActionResult<T, Metadata>> =>
        source.pipe(
            mergeMap((action) =>
                deps.injector
                    .get<TemplateFormService>(TemplateFormService)
                    .retrieveMetadata<Metadata>(
                        action.templateId,
                        new MetadataModelTransformer(),
                    )
                    .pipe(map((data) => ({ action, data }))),
            ),
        );

const _listData =
    (deps: Dependencies) =>
    <T extends { templateId: number; args: Args }>(
        source: Observable<T>,
    ): Observable<ActionResult<T, Array<Data<PayloadExtended>>>> =>
        source.pipe(
            mergeMap((action) =>
                deps.injector
                    .get<TemplateFormService>(TemplateFormService)
                    .listData<PayloadExtended>(
                        action.templateId,
                        action.args,
                        new PayloadModelTransformer(),
                    )
                    .pipe(map((data) => ({ action, data }))),
            ),
        );

const _createData =
    (deps: Dependencies) =>
    (
        source: Observable<FormSaveAction>,
    ): Observable<ActionResult<FormSaveAction, string>> =>
        source.pipe(
            mergeMap((action) =>
                deps.injector
                    .get<DataFormService>(DataFormService)
                    .createData<Payload>(
                        action.data,
                        new PayloadModelTransformer(),
                    )
                    .pipe(map((data) => ({ action, data }))),
            ),
        );

const _retrievePayload =
    (deps: Dependencies) =>
    (
        source: Observable<number>,
    ): Observable<ActionResult<number, PayloadExtended>> =>
        source.pipe(
            mergeMap((action) =>
                deps.injector
                    .get<DataFormService>(DataFormService)
                    .retrievePayload<PayloadExtended>(
                        action,
                        new PayloadModelTransformer(),
                    )
                    .pipe(map((data) => ({ action, data }))),
            ),
        );

const _retrieveHierarchyInfo =
    (deps: Dependencies) =>
    <T extends { userId: number; maxDepth: number }>(
        source: Observable<T>,
    ): Observable<ActionResult<T, HierarchyInfo>> =>
        source.pipe(
            mergeMap((action) =>
                deps.injector
                    .get<TdcfService>(TdcfService)
                    .retrieveHierarchyInfo(action.userId, action.maxDepth)
                    .pipe(map((data) => ({ action, data }))),
            ),
        );

const _generateManagerReport =
    (deps: Dependencies) =>
    <T extends ReportArgs>(
        source: Observable<T>,
    ): Observable<ActionResult<T, ManagerReportDataItem[]>> =>
        source.pipe(
            mergeMap((action) =>
                deps.injector
                    .get<TdcfService>(TdcfService)
                    .generateManagerReport(action)
                    .pipe(map((data) => ({ action, data }))),
            ),
        );

const _groupActions = <T extends Action<ActionType>, U extends { root: T }>(
    rootAction: T,
    method?: 'concat' | 'fork',
    ...items: Array<{
        key: string;
        action: any;
        obs: OperatorFunction<any, any>;
    }>
): Observable<U> => {
    const group = { root: rootAction };
    if (method === 'concat') {
        return items.reduce((chain: Observable<any>, item) => {
            const next = of({ action: item.action, group }).pipe(
                item.obs,
                map((ret) => {
                    group[item.key] = ret;
                    return group;
                }),
            );
            return chain ? chain.pipe(concatMap(() => next)) : next;
        }, null);
        // return concat(...items.map(item => of(item.action)
        //     .pipe(
        //         item.obs,
        //         map(ret => {
        //             group[item.key] = ret;
        //             return group as any as U;
        //         })
        //     )));
    }
    if (method === 'fork') {
        return forkJoin(
            ...items.map((item) =>
                of(item.action).pipe(
                    item.obs,
                    map((ret) => {
                        group[item.key] = ret;
                        return group;
                    }),
                ),
            ),
        ).pipe(map((groupMirror) => groupMirror[0]));
    }
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const _injectText = (deps: Dependencies, texts: Text[]) => {
    const tl = deps.injector.get<TranslateService>(TranslateService);
    const keys = [
        'form_memberInstruction',
        'form_lookingBackLabel',
        'form_lookingBackPlaceholder',
        'form_lookingBackDescription',
        'form_lookingForwardInstruction',
        'form_lookingForwardLabel',
        'form_lookingForwardPlaceholder',
        'form_lookingForwardDescription',
        'form_managerInstruction',
        'form_conversationSummaryLabel',
        'form_conversationSummaryPlaceholder',
        'form_reminder',
    ];
    keys.forEach((k) => {
        const text = texts.find((t) => t.key === k);
        if (!text) return;
        tl.set(k, text.default);
    });
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const retrieveInitData = (
    action: ActionsObservable<RetrieveInitDataAction>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.TDCF_FORM__RETRIEVE_INIT_DATA),
        switchMap((action) =>
            _groupActions<
                RetrieveInitDataAction,
                {
                    root: RetrieveInitDataAction;
                    metadata: ActionResult<any, Metadata>;
                    me: ActionResult<any, Array<Data<PayloadExtended>>>;
                    team: ActionResult<any, Array<Data<PayloadExtended>>>;
                    info: ActionResult<any, HierarchyInfo>;
                    hierarchy: ActionResult<any, HierarchyInfo>;
                }
            >(
                action,
                'fork',
                {
                    key: 'metadata',
                    action: { templateId: action.templateId },
                    obs: _retrieveMetadata(deps),
                },
                {
                    key: 'me',
                    action: {
                        templateId: action.templateId,
                        args: { args: { userId: action.userId } },
                    },
                    obs: _listData(deps),
                },
                {
                    key: 'team',
                    action: {
                        templateId: action.templateId,
                        args: { args: { userId: action.userId, team: true } },
                    },
                    obs: _listData(deps),
                },
                {
                    key: 'info',
                    action: { userId: action.userId, maxDepth: 1 },
                    obs: _retrieveHierarchyInfo(deps),
                },
                {
                    key: 'hierarchy',
                    action: { userId: action.userId, maxDepth: 2 },
                    obs: _retrieveHierarchyInfo(deps),
                },
            ).pipe(
                tap((group) => {
                    if (!group.metadata) return;

                    _injectText(deps, group.metadata.data.texts);

                    let isSelf = false;
                    try {
                        isSelf =
                            group.info.data.userId ===
                            deps.injector.get<AuthService>(AuthService).user.id;
                    } catch {}

                    const now = new Date();
                    const dr = reviewDateRangeFor(
                        now,
                        group.metadata.data.reviewDateRanges,
                    );
                    if (
                        isSelf &&
                        group.info.data.manager &&
                        !hasReviewWithin(dr, group.me.data)
                    ) {
                        // TODO return PayloadExtended instead of Data<PayloadExtended>
                        // TODO revise dummy data
                        group.me.data.push({
                            dataId: null,
                            payload: {
                                threadGuid: null,
                                memberId: group.info.data.userId,
                                managerId: group.info.data.manager.userId,
                                lookingBack: null,
                                lookingForward: null,
                                conversationSummary: null,
                                created: now,
                                flowState: FlowState.MEMBER_DRAFT,
                                custodyUserId: group.root.userId,
                            },
                            templateId: null,
                        });
                    }
                    if (!isSelf) {
                        // no drafts should be present, but check anyways
                        const exclude = [
                            FlowState.MEMBER_DRAFT,
                            FlowState.MANAGER_DRAFT,
                        ];
                        // eslint-disable-next-line @typescript-eslint/prefer-includes
                        group.me.data = group.me.data.filter(
                            // eslint-disable-next-line @typescript-eslint/prefer-includes
                            (d) => exclude.indexOf(d.payload.flowState) === -1,
                        );
                    }
                }),
                map((group) =>
                    retrieveInitDataResult(
                        group.root.templateId,
                        group.root.userId,
                        group.metadata.data,
                        group.me.data,
                        group.team.data,
                        group.info.data,
                        group.hierarchy.data,
                    ),
                ),
                catchError(() =>
                    of(
                        retrieveInitDataResult(
                            null,
                            null,
                            null,
                            null,
                            null,
                            null,
                            null,
                        ),
                    ),
                ),
            ),
        ),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const formSave = (
    action: ActionsObservable<FormSaveAction>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.TDCF_FORM__FORM_SAVE),
        switchMap((action) =>
            _groupActions<
                FormSaveAction,
                {
                    root: FormSaveAction;
                    data: ActionResult<any, string>;
                    payload: ActionResult<any, PayloadExtended>;
                }
            >(
                action,
                'concat',
                {
                    key: 'data',
                    action,
                    obs: switchMap((ag) =>
                        of(ag.action).pipe(_createData(deps)),
                    ),
                },
                {
                    key: 'payload',
                    action,
                    obs: switchMap((ag) => {
                        let obs = of({ action: ag.action, data: null });
                        try {
                            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                            const dataId = parseInt(
                                ag.group.data.data.substring(
                                    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                                    ag.group.data.data.lastIndexOf('/') + 1,
                                ),
                            );
                            obs = of(dataId).pipe(
                                _retrievePayload(deps),
                                map((ret) => {
                                    // TODO revise
                                    if (ret.data) {
                                        (ret.data as any).dataId = dataId;
                                    }
                                    return ret;
                                }),
                            );
                        } catch (e) {}
                        return obs;
                    }),
                },
            ).pipe(
                map((group) =>
                    formSaveResult(
                        group.root.key,
                        group.root.data,
                        group.data.data,
                        group.payload.data,
                    ),
                ),
                catchError(() => of(formSaveResult(null, null, null, null))),
            ),
        ),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const printRetrieveInitData = (
    action: ActionsObservable<PrintRetrieveInitDataAction>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.TDCF_FORM__PRINT_RETRIEVE_INIT_DATA),
        switchMap((action) =>
            _groupActions<
                PrintRetrieveInitDataAction,
                {
                    root: PrintRetrieveInitDataAction;
                    metadata: ActionResult<any, Metadata>;
                    payload: ActionResult<any, PayloadExtended>;
                }
            >(
                action,
                'fork',
                {
                    key: 'metadata',
                    action: { templateId: action.templateId },
                    obs: _retrieveMetadata(deps),
                },
                {
                    key: 'payload',
                    action: action.dataId,
                    obs: _retrievePayload(deps),
                },
            ).pipe(
                tap((group) => {
                    if (group.metadata) {
                        _injectText(deps, group.metadata.data.texts);
                    }
                }),
                map((group) =>
                    printRetrieveInitDataResult(
                        group.root.templateId,
                        group.root.dataId,
                        group.metadata.data,
                        group.payload.data,
                    ),
                ),
                catchError(() =>
                    of(printRetrieveInitDataResult(null, null, null, null)),
                ),
            ),
        ),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const reportRetrieveInitData = (
    action: ActionsObservable<ReportRetrieveInitDataAction>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.TDCF_FORM__REPORT_RETRIEVE_INIT_DATA),
        switchMap((action) =>
            of(action).pipe(
                _retrieveMetadata(deps),
                map((data) =>
                    reportRetrieveInitResultData(
                        data.action.templateId,
                        data.data,
                    ),
                ),
                catchError(() => of(reportRetrieveInitResultData(null, null))),
            ),
        ),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const reportGenerate = (
    action: ActionsObservable<ReportGenerateAction>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.TDCF_FORM__REPORT_GENERATE),
        switchMap((action) =>
            of(action).pipe(
                _generateManagerReport(deps),
                map((data) =>
                    reportGenerateResult(
                        data.action.templateId,
                        data.action.userId,
                        data.action.reviewDateRanges,
                        data.action.directTeamOnly,
                        data.data,
                    ),
                ),
                catchError(() =>
                    of(reportGenerateResult(null, null, null, null, null)),
                ),
            ),
        ),
    );
